// src/pages/Jour.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";

const months = [
  "Januari","Februari","Mars","April","Maj","Juni",
  "Juli","Augusti","September","Oktober","November","December"
];
const dayNames = ["sön", "mån", "tis", "ons", "tors", "fre", "lör"];

// ---- ISO Helpers ----

// Hämta ISO-vecka (måndag som veckostart)
function getISOWeek(date) {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = tmp.getUTCDay() || 7; // söndag=7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
}

// Hämtar måndag–söndag för given år+vecka
function getISOWeekRange(year, week) {
  const simple = new Date(Date.UTC(year, 0, 4 + (week - 1) * 7));
  const day = simple.getUTCDay() || 7;
  const weekStart = new Date(simple);
  weekStart.setUTCDate(simple.getUTCDate() - day + 1);
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
  return [new Date(weekStart), new Date(weekEnd)];
}

// Formateringar
function fmtInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtDM(d) {
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "numeric" });
}

export default function Jour() {
  const { user, fetchJour, adminAddJour, adminDeleteJour, adminUpdateJour, fetchAssignableUsers } = useAuth();
  const [jours, setJours] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [assign, setAssign] = useState(null);

  const [currentMonth, setCurrentMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const load = async () => {
    setLoading(true);
    const j = await fetchJour();
    setJours(j);
    if (user?.role === "admin") {
      const u = await fetchAssignableUsers();
      setUsers(u);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Alla veckor som berör aktuell månad
  const weeksInMonth = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const set = new Set();
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      set.add(getISOWeek(new Date(d)));
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [year, month]);

  const weeksData = useMemo(() => {
    const map = new Map();
    for (const w of weeksInMonth) {
      const [wStart, wEnd] = getISOWeekRange(year, w);
      map.set(w, { weekStart: wStart, weekEnd: wEnd, items: [] });
    }
    for (const j of jours) {
      const s = new Date(j.startDate);
      const e = new Date(j.endDate);
      for (const w of weeksInMonth) {
        const entry = map.get(w);
        if (!entry) continue;
        const { weekStart, weekEnd } = entry;
        if (e < weekStart || s > weekEnd) continue;
        entry.items.push(j);
      }
    }
    return map;
  }, [jours, weeksInMonth, year, month]);

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const openAssign = (week) => {
    const { weekStart, weekEnd } = weeksData.get(week);
    setAssign({
      week,
      weekStart,
      weekEnd,
      userId: "",
      startDate: fmtInput(weekStart),
      endDate: fmtInput(weekEnd)
    });
  };
  const saveAssign = async () => {
    if (!assign?.userId || !assign?.startDate || !assign?.endDate) return;
    await adminAddJour({ userId: assign.userId, startDate: assign.startDate, endDate: assign.endDate });
    setAssign(null);
    load();
  };

  const saveEdit = async () => {
    await adminUpdateJour(editing.id, {
      userId: editing.userId,
      startDate: editing.startDate,
      endDate: editing.endDate
    });
    setEditing(null);
    load();
  };
  const deleteEdit = async () => {
    if (!window.confirm("Ta bort jourpasset?")) return;
    await adminDeleteJour(editing.id);
    setEditing(null);
    load();
  };

  // Label för veckodagar
  function labelForWeekSpan(j, weekStart, weekEnd) {
    const s = new Date(j.startDate);
    const e = new Date(j.endDate);
    const clipStart = s < weekStart ? new Date(weekStart) : new Date(s);
    const clipEnd = e > weekEnd ? new Date(weekEnd) : new Date(e);

    const covered = [];
    for (let d = new Date(clipStart); d <= clipEnd; d.setDate(d.getDate() + 1)) {
      covered.push(d.getDay());
    }
    const names = covered.map((n) => dayNames[n]);
    const uniq = [...new Set(names)];
    if (uniq.length === 7) return "(hela veckan)";
    return `(${uniq.join(", ")})`;
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-1">Jourhantering</h3>
      {user?.role === "admin" && (
        <p className="text-muted small">
          💡 Tips: Klicka på ett namn för att ändra/ta bort. Använd
          <span className="ms-1 badge bg-light text-dark">+</span> för att lägga till fler pass i samma vecka.
        </p>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={prevMonth}>‹</button>
        <h4 className="mb-0">{months[month]} {year}</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={nextMonth}>›</button>
      </div>

      {loading ? (
        <div>Laddar jour…</div>
      ) : (
        <div className="card">
          <ul className="list-group list-group-flush">
            {[...weeksData.entries()].map(([week, info]) => {
              const { weekStart, weekEnd, items } = info;
              return (
                <li key={week} className="list-group-item">
                  <div className="d-flex align-items-center mb-2">
                    <span className="fw-bold me-2">v{week}</span>
                    <span className="text-muted small">{fmtDM(weekStart)} – {fmtDM(weekEnd)}</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {items.length > 0 ? (
                      items.map((j) => (
                        <span key={j.id}
                          className="badge bg-light text-dark p-2 d-inline-flex align-items-center"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setEditing({
                              id: j.id,
                              userId: j.user?.id,
                              startDate: j.startDate.slice(0, 10),
                              endDate: j.endDate.slice(0, 10)
                            })
                          }
                        >
                          <Avatar initials={j.user?.avatar?.initials} color={j.user?.avatar?.color} size={18} />
                          <span className="ms-1">{j.user?.firstName}</span>
                          <span className="ms-1 text-muted small">{labelForWeekSpan(j, weekStart, weekEnd)}</span>
                          {user?.role === "admin" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary ms-2 py-0 px-1"
                              onClick={(e) => { e.stopPropagation(); openAssign(week); }}
                            >
                              +
                            </button>
                          )}
                        </span>
                      ))
                    ) : (
                      user?.role === "admin" && (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openAssign(week)}>
                          Tilldela jour
                        </button>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* EDIT MODAL */}
      {editing && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ändra jourpass</h5>
                <button className="btn-close" onClick={() => setEditing(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Användare</label>
                  <select className="form-select"
                    value={editing.userId}
                    onChange={(e) => setEditing({ ...editing, userId: e.target.value })}
                  >
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Start</label>
                  <input type="date" className="form-control" value={editing.startDate}
                    onChange={e => setEditing({ ...editing, startDate: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Slut</label>
                  <input type="date" className="form-control" value={editing.endDate}
                    onChange={e => setEditing({ ...editing, endDate: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-danger me-auto" onClick={deleteEdit}>Ta bort</button>
                <button className="btn btn-secondary" onClick={() => setEditing(null)}>Avbryt</button>
                <button className="btn btn-primary" onClick={saveEdit}>Spara</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGN MODAL */}
      {assign && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Tilldela jour (v{assign.week})</h5>
                <button className="btn-close" onClick={() => setAssign(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Användare</label>
                  <select className="form-select"
                    value={assign.userId}
                    onChange={(e) => setAssign({ ...assign, userId: e.target.value })}
                  >
                    <option value="">Välj…</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Start</label>
                    <input type="date" className="form-control"
                      min={fmtInput(assign.weekStart)} max={fmtInput(assign.weekEnd)}
                      value={assign.startDate}
                      onChange={e => {
                        let v = e.target.value;
                        if (v > assign.endDate) v = assign.endDate;
                        setAssign({ ...assign, startDate: v });
                      }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Slut</label>
                    <input type="date" className="form-control"
                      min={assign.startDate} max={fmtInput(assign.weekEnd)}
                      value={assign.endDate}
                      onChange={e => {
                        let v = e.target.value;
                        if (v < assign.startDate) v = assign.startDate;
                        setAssign({ ...assign, endDate: v });
                      }} />
                  </div>
                </div>
                <div className="form-text mt-2">
                  Veckans spann: {fmtDM(assign.weekStart)} – {fmtDM(assign.weekEnd)}.
                  Du kan skapa flera delpass samma vecka.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setAssign(null)}>Avbryt</button>
                <button className="btn btn-primary" disabled={!assign.userId} onClick={saveAssign}>Spara</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
